import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWorkspace, useCard, useDecks, useUndo, useStorage } from '../hooks/useApi';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveOrShareFile } from '../lib/fileUtils';
import { 
  ArrowLeft, Edit, Trash2, Plus, X, 
  ArrowLeftRight, ChevronDown, ChevronUp, ExternalLink, FolderPlus, Undo2, Moon, Sun,
  FileText, Printer, Download, Eye
} from 'lucide-react';

// Card Editor Component (inline)
function CardEditorApi({ isOpen, onClose, card, onSave }) {
  const storage = useStorage();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fieldIdCounter, setFieldIdCounter] = useState(0);

  useEffect(() => {
    if (card) {
      setTitle(card.title || '');
      setBody(card.body || '');
      const fieldList = Object.entries(card.fields || {}).map(([key, val], idx) => ({
        id: idx,
        key,
        value: typeof val === 'object' ? val.value : val
      }));
      setFields(fieldList.length > 0 ? fieldList : [{ id: 0, key: '', value: '' }]);
      setFieldIdCounter(fieldList.length || 1);
    }
  }, [card, isOpen]);

  const addField = () => {
    setFields([...fields, { id: fieldIdCounter, key: '', value: '' }]);
    setFieldIdCounter(prev => prev + 1);
  };
  
  const removeField = (id) => {
    const newFields = fields.filter(f => f.id !== id);
    setFields(newFields.length > 0 ? newFields : [{ id: fieldIdCounter, key: '', value: '' }]);
    if (newFields.length === 0) setFieldIdCounter(prev => prev + 1);
  };

  const updateField = (id, key, value) => {
    setFields(fields.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    // Combine duplicate field names into comma-separated values
    const fieldObj = {};
    fields.forEach(f => {
      const key = f.key.trim();
      const value = f.value.trim();
      if (key && value) {
        if (fieldObj[key]) {
          // Append to existing value with comma
          fieldObj[key] = fieldObj[key] + ', ' + value;
        } else {
          fieldObj[key] = value;
        }
      }
    });

    // Validate: card must have notes OR at least one field
    const hasBody = body.trim().length > 0;
    const hasFields = Object.keys(fieldObj).length > 0;
    
    if (!hasBody && !hasFields) {
      toast.error('Card must have at least one field or a note to be saved');
      return;
    }

    setSaving(true);
    try {
      await storage.updateCard(card.id, {
        title: title.trim(),
        body: body.trim(),
        fields: fieldObj
      });

      onSave();
    } catch (error) {
      toast.error(error.message || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Card title" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Optional notes..." rows={3} />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Fields</Label>
              <Button variant="outline" size="sm" onClick={addField}>
                <Plus className="h-3 w-3 mr-1" /> Add Field
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field) => (
                <div key={field.id} className="flex gap-2 items-center">
                  <Input 
                    value={field.key} 
                    onChange={(e) => updateField(field.id, 'key', e.target.value)}
                    placeholder="Field name"
                    className="flex-1"
                  />
                  <span className="text-muted-foreground">:</span>
                  <Input 
                    value={field.value} 
                    onChange={(e) => updateField(field.id, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeField(field.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Card'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FocusMode() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const storage = useStorage();
  const { theme, toggleTheme } = useTheme();
  const { currentWorkspace, loading: wsLoading } = useWorkspace();
  const { card, loading, refetch } = useCard(cardId);
  const { decks } = useDecks();
  const { lastDeleted, performUndo, checkUndo } = useUndo();
  const viewModeRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [loadingDeletionPreview, setLoadingDeletionPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [referencedBy, setReferencedBy] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [fieldSortOrder, setFieldSortOrder] = useState('az');
  const [expandedFields, setExpandedFields] = useState(new Set());
  const [swappedFields, setSwappedFields] = useState(new Set()); // Track visually swapped fields

  // Fetch referenced by cards
  useEffect(() => {
    async function fetchRefs() {
      if (!card || !currentWorkspace) return;
      setLoadingRefs(true);
      try {
        const refs = await storage.getCardReferences(card.id);
        setReferencedBy(refs);
      } catch (error) {
        console.error('Failed to fetch references:', error);
      } finally {
        setLoadingRefs(false);
      }
    }
    fetchRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, currentWorkspace?.id]);

  // Sort fields - always sort by the displayed left column
  const sortedFields = useMemo(() => {
    if (!card?.fields) return [];
    const entries = Object.entries(card.fields);
    return entries.sort((a, b) => {
      // Get display values based on swap state
      const aSwapped = swappedFields.has(a[0]);
      const bSwapped = swappedFields.has(b[0]);
      const aLeft = aSwapped ? (typeof a[1] === 'object' ? a[1].value : a[1]) : a[0];
      const bLeft = bSwapped ? (typeof b[1] === 'object' ? b[1].value : b[1]) : b[0];
      const cmp = String(aLeft).localeCompare(String(bLeft));
      return fieldSortOrder === 'az' ? cmp : -cmp;
    });
  }, [card?.fields, fieldSortOrder, swappedFields]);

  // Toggle visual swap for a field (purely cosmetic - no data change)
  const toggleFieldSwap = (fieldName) => {
    setSwappedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  // Delete handlers
  const fetchDeletionPreview = async () => {
    setLoadingDeletionPreview(true);
    try {
      const preview = await storage.getCardDeletionPreview(card.id);
      setDeletionPreview(preview);
    } catch (error) {
      setDeletionPreview(null);
    } finally {
      setLoadingDeletionPreview(false);
    }
  };

  const openDeleteDialog = () => {
    setIsDeleteOpen(true);
    fetchDeletionPreview();
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await storage.deleteCard(card.id, true);
      const fieldsRemoved = result?.fields_removed || 0;
      toast.success(fieldsRemoved > 0 
        ? `Card deleted and ${fieldsRemoved} related field(s) removed`
        : 'Card deleted'
      );
      checkUndo();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete card');
    } finally {
      setIsDeleting(false);
    }
  };

  // Undo handler
  const handleUndo = async () => {
    try {
      const result = await performUndo();
      toast.success(result.message);
      refetch();
    } catch (error) {
      toast.error('Undo failed');
    }
  };

  // Navigate to linked card
  const navigateToCard = async (title) => {
    try {
      const cards = await storage.searchCards(title);
      const foundCard = cards.find(c => c.title.toLowerCase() === title.toLowerCase());
      if (foundCard) {
        navigate(`/card/${foundCard.id}`);
      } else {
        toast.error('Card not found');
      }
    } catch (error) {
      toast.error('Failed to find card');
    }
  };

  // Toggle field expansion
  const toggleFieldExpansion = (fieldName) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldName)) {
      newExpanded.delete(fieldName);
    } else {
      newExpanded.add(fieldName);
    }
    setExpandedFields(newExpanded);
  };

  // Check if value has multiple components
  const getValueComponents = (value) => {
    if (!value || typeof value !== 'string') return [value];
    if (value.includes(',')) {
      return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return [value];
  };

  // View Mode: Print function - opens clean print window with only card content
  const handlePrint = () => {
    const printContent = viewModeRef.current;
    if (!printContent) {
      toast.error('Nothing to print');
      return;
    }
    
    // Create a new window with just the card content
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow popups for printing');
      return;
    }
    
    // Build clean HTML for printing
    const printHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${card.title} - MindDeck</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Georgia, 'Times New Roman', serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { font-size: 28px; margin-bottom: 16px; color: #1a1a1a; border-bottom: 2px solid #4A5D4F; padding-bottom: 12px; }
    h2 { font-size: 18px; margin: 24px 0 12px 0; color: #333; }
    .notes { color: #555; font-style: italic; margin-bottom: 24px; padding-left: 16px; border-left: 3px solid #ccc; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
    th { background-color: #4A5D4F; color: white; font-weight: 600; }
    tr:nth-child(even) { background-color: #f9f9f9; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 12px; color: #888; }
    @media print {
      body { padding: 0; }
      th { background-color: #4A5D4F !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr:nth-child(even) { background-color: #f9f9f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <h1>${card.title}</h1>
  ${card.body ? `<p class="notes">${card.body}</p>` : ''}
  ${Object.keys(card.fields || {}).length > 0 ? `
  <h2>Fields</h2>
  <table>
    <thead>
      <tr><th>Field</th><th>Value</th></tr>
    </thead>
    <tbody>
      ${Object.entries(card.fields || {}).map(([key, val]) => {
        const value = typeof val === 'object' ? val.value : val;
        return `<tr><td>${key}</td><td>${value}</td></tr>`;
      }).join('')}
    </tbody>
  </table>` : ''}
  <div class="footer">Generated from MindDeck • ${new Date().toLocaleDateString()}</div>
</body>
</html>`;
    
    printWindow.document.write(printHtml);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      // Close window after print dialog closes
      printWindow.onafterprint = () => printWindow.close();
    };
  };

  // View Mode: Save as different formats (mobile-compatible)
  const handleSaveAs = async (format) => {
    toast.info(`Saving as ${format}...`);
    
    if (!card) {
      toast.error('No card to save');
      return;
    }
    
    const timestamp = new Date().toISOString().split('T')[0];
    const fileName = `${card.title.replace(/[^a-z0-9]/gi, '_')}-${timestamp}`;
    
    // Prepare card data
    const fields = Object.entries(card.fields || {}).map(([key, val]) => ({
      field: key,
      value: typeof val === 'object' ? val.value : val
    }));

    try {
      if (format === 'xlsx') {
        const data = [
          { Field: 'Title', Value: card.title },
          { Field: 'Notes', Value: card.body || '' },
          ...fields.map(f => ({ Field: f.field, Value: f.value }))
        ];
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Card');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        await saveOrShareFile(blob, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      } else if (format === 'docx') {
        const rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0\\fswiss Arial;}}
{\\colortbl;\\red74\\green93\\blue79;}
\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440

{\\pard\\fs48\\b ${card.title}\\par}
${card.body ? `{\\pard\\fs24\\i\\cf0 ${card.body}\\par}` : ''}
\\par
${fields.length > 0 ? `{\\pard\\fs28\\b Fields\\par}
{\\trowd\\trgaph100\\trleft0
\\cellx4500\\cellx9000
{\\pard\\intbl\\b Field\\cell}
{\\pard\\intbl\\b Value\\cell}
\\row}
${fields.map(f => `{\\trowd\\trgaph100\\trleft0
\\cellx4500\\cellx9000
{\\pard\\intbl ${f.field.replace(/[\\{}]/g, '')}\\cell}
{\\pard\\intbl ${f.value.replace(/[\\{}]/g, '')}\\cell}
\\row}`).join('\n')}` : ''}
\\par
{\\pard\\fs18\\i Generated from MindDeck - ${new Date().toLocaleDateString()}\\par}
}`;
        const blob = new Blob([rtfContent], { type: 'application/rtf' });
        await saveOrShareFile(blob, `${fileName}.rtf`, 'application/rtf');

      } else if (format === 'pdf') {
        toast.info('Select "Save as PDF" in the print dialog');
        setTimeout(() => handlePrint(), 300);
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save file: ' + error.message);
    }
  };

  if (wsLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Card not found</p>
        <Button onClick={() => navigate('/')}>Go Back</Button>
      </div>
    );
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx > 0) {
      navigate(-1);
    } else {
      navigate(1);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="focus-mode"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              {lastDeleted && (
                <Button variant="ghost" size="sm" onClick={handleUndo} className="gap-2" data-testid="undo-btn">
                  <Undo2 className="h-4 w-4" />
                  Undo
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button 
                variant="outline" 
                onClick={openDeleteDialog}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
                data-testid="delete-card-btn"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Card Title & Notes */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{card.title}</h1>
              {card.body && (
                <p className="text-muted-foreground">{card.body}</p>
              )}
            </div>
            <Button variant="outline" onClick={() => setIsEditorOpen(true)} className="gap-2 shrink-0" data-testid="edit-card-btn">
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button variant="outline" onClick={() => setViewMode(true)} className="gap-2 shrink-0" data-testid="view-mode-btn">
              <Eye className="h-4 w-4" />
              View Mode
            </Button>
          </div>
          
          {/* Deck badges */}
          {card.decks?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {card.decks.map(deckId => {
                const deck = decks.find(d => d.id === deckId);
                return deck ? (
                  <Badge key={deckId} variant="secondary">{deck.name}</Badge>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* Fields Section */}
        <Card className="mb-8">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Fields ({sortedFields.length})</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFieldSortOrder(fieldSortOrder === 'az' ? 'za' : 'az')}
                className="text-xs font-medium"
              >
                {fieldSortOrder === 'az' ? 'A-Z' : 'Z-A'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {sortedFields.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No fields yet</p>
            ) : (
              <div className="space-y-2">
                {sortedFields.map(([fieldName, fieldData]) => {
                  const value = typeof fieldData === 'object' ? fieldData.value : fieldData;
                  const isExpanded = expandedFields.has(fieldName);
                  const isSwapped = swappedFields.has(fieldName);
                  
                  // Display values based on swap state (purely visual)
                  const displayLeft = isSwapped ? value : fieldName;
                  const displayRight = isSwapped ? fieldName : value;

                  // Chevron expands if either side has multiple comma-separated values
                  const leftComponents = getValueComponents(displayLeft);
                  const rightComponents = getValueComponents(displayRight);
                  const hasMultiple = leftComponents.length > 1 || rightComponents.length > 1;

                  return (
                    <div key={fieldName} className="border rounded p-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {/* Left side (field name or value if swapped) */}
                          <button
                            onClick={() => navigateToCard(displayLeft)}
                            className="bg-primary/10 hover:bg-primary/20 text-primary px-2 py-1 rounded text-sm font-medium transition-colors text-left break-words"
                            style={{ maxWidth: '45%', wordBreak: 'break-word' }}
                          >
                            {displayLeft}
                          </button>
                          <span className="text-muted-foreground mt-1">:</span>
                          {/* Right side (value or field name if swapped) */}
                          <button
                            onClick={() => navigateToCard(displayRight)}
                            className="bg-secondary hover:bg-secondary/80 px-2 py-1 rounded text-sm transition-colors flex-1 text-left break-words"
                            style={{ wordBreak: 'break-word' }}
                          >
                            {displayRight}
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-1 shrink-0 mt-0.5">
                          {/* Expand button for multi-value */}
                          {hasMultiple && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleFieldExpansion(fieldName)}
                              className="h-7 w-7"
                            >
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </Button>
                          )}
                          
                          {/* Swap button - purely visual toggle */}
                          <Button
                            variant={isSwapped ? "secondary" : "ghost"}
                            size="icon"
                            onClick={() => toggleFieldSwap(fieldName)}
                            className="h-7 w-7"
                            title="Toggle field/value display"
                          >
                            <ArrowLeftRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expanded components — show both sides if either has multiple values */}
                      {hasMultiple && isExpanded && (
                        <div className="mt-3 pl-4 border-l-2 border-muted space-y-3">
                          {leftComponents.length > 1 && (
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">{displayLeft.length > 30 ? displayLeft.slice(0, 30) + '…' : displayLeft}</div>
                              <div className="space-y-1">
                                {leftComponents.map((comp, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => navigateToCard(comp)}
                                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    {comp}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {rightComponents.length > 1 && (
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">{displayRight.length > 30 ? displayRight.slice(0, 30) + '…' : displayRight}</div>
                              <div className="space-y-1">
                                {rightComponents.map((comp, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => navigateToCard(comp)}
                                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    {comp}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referenced By Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Referenced By ({referencedBy.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRefs ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : referencedBy.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No cards reference this card</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {referencedBy.map(refCard => (
                  <Link
                    key={refCard.id}
                    to={`/card/${refCard.id}`}
                    className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="font-medium">{refCard.title}</div>
                    {refCard.body && (
                      <p className="text-sm text-muted-foreground line-clamp-1">{refCard.body}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Editor Dialog */}
      <CardEditorApi
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        card={card}
        onSave={() => {
          toast.success('Card updated');
          refetch();
          setIsEditorOpen(false);
        }}
      />
      
      {/* Delete Confirmation with Impact Preview */}
      <AlertDialog open={isDeleteOpen} onOpenChange={(open) => {
        setIsDeleteOpen(open);
        if (!open) setDeletionPreview(null);
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Card
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>Are you sure you want to delete <strong>"{card.title}"</strong>? This action cannot be undone.</p>
                
                {loadingDeletionPreview && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-sm">Checking for related fields...</span>
                  </div>
                )}
                
                {deletionPreview?.affected_cards?.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-2">
                      This will also remove {deletionPreview.total_fields_removed} field(s) from {deletionPreview.affected_cards.length} other card(s):
                    </p>
                    <ScrollArea className="max-h-48">
                      {deletionPreview.affected_cards.map((affected) => (
                        <div key={affected.card_id} className="bg-background/50 rounded p-2 text-sm mb-2">
                          <div className="font-medium">{affected.card_title}</div>
                          <ul className="mt-1">
                            {affected.fields_to_remove.map((field, idx) => (
                              <li key={idx} className="text-muted-foreground text-xs flex items-center gap-1">
                                <span className="text-red-500">✕</span>
                                <span className="font-mono">{field.field_name}: {field.field_value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                )}
                
                {deletionPreview && !deletionPreview.affected_cards?.length && (
                  <p className="text-sm text-muted-foreground">✓ No other cards reference this card.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground" 
              data-testid="confirm-delete-btn"
              disabled={isDeleting || loadingDeletionPreview}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Deleting...
                </>
              ) : deletionPreview?.total_fields_removed > 0 ? (
                `Delete Card & ${deletionPreview.total_fields_removed} Field(s)`
              ) : (
                'Delete Card'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Mode - Full Screen Document View */}
      {viewMode && (
        <div className="view-mode-container fixed inset-0 bg-white dark:bg-gray-900 z-[100] overflow-auto">
          {/* View Mode Header - Hidden when printing */}
          <header className="view-mode-header sticky top-0 bg-white dark:bg-gray-900 border-b px-4 py-3 flex items-center justify-between gap-4">
            <Button variant="outline" onClick={() => setViewMode(false)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to App
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handlePrint} className="gap-2">
                <Printer className="h-4 w-4" />
                Print
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="save-as-dropdown">
                    <Download className="h-4 w-4" />
                    Save As
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[200]">
                  <DropdownMenuItem 
                    onClick={(e) => { e.preventDefault(); handleSaveAs('docx'); }}
                    className="cursor-pointer"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Document (.rtf)
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => { e.preventDefault(); handleSaveAs('xlsx'); }}
                    className="cursor-pointer"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Spreadsheet (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => { e.preventDefault(); handleSaveAs('pdf'); }}
                    className="cursor-pointer"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    PDF (via Print)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Document Content */}
          <div ref={viewModeRef} className="view-mode-content max-w-3xl mx-auto p-8">
            {/* Title */}
            <h1 className="text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100 print:text-black">
              {card.title}
            </h1>

            {/* Notes */}
            {card.body && (
              <div className="mb-8 text-lg text-gray-600 dark:text-gray-400 print:text-gray-600 italic border-l-4 border-gray-300 pl-4">
                {card.body}
              </div>
            )}

            {/* Fields as a clean table */}
            {Object.keys(card.fields || {}).length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200 print:text-black">
                  Fields
                </h2>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-800 print:bg-gray-100">
                      <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 print:text-black print:border-gray-400">
                        Field
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 print:text-black print:border-gray-400">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(card.fields || {}).map(([fieldName, fieldData], index) => {
                      const value = typeof fieldData === 'object' ? fieldData.value : fieldData;
                      return (
                        <tr key={fieldName} className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-gray-100 print:text-black print:border-gray-400 font-medium">
                            {fieldName}
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300 print:text-black print:border-gray-400">
                            {value}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer with metadata */}
            <div className="mt-12 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 print:text-gray-500">
              <p>Generated from MindDeck • {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FocusMode;
